import React, { useState } from 'react';
import { Row, Col, Card, Table, Badge, ProgressBar, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './QCReports.css';

const QCReports = () => {
  const [selectedProject, setSelectedProject] = useState('ecommerce');
  const [dateRange, setDateRange] = useState('30days');
  const [selectedReport, setSelectedReport] = useState('execution-summary');

  // Mock data
  const reportTemplates = [
    {
      id: 'execution-summary',
      title: 'Test Execution Summary',
      icon: 'fa-chart-bar',
      description: 'Overall execution metrics and test case results'
    },
    {
      id: 'pass-fail-trend',
      title: 'Pass vs Fail Trend',
      icon: 'fa-chart-line',
      description: 'Historical pass/fail rate analysis over time'
    },
    {
      id: 'tester-productivity',
      title: 'Tester Productivity',
      icon: 'fa-user-check',
      description: 'Individual and team performance metrics'
    },
    {
      id: 'release-readiness',
      title: 'Release Readiness Report',
      icon: 'fa-rocket',
      description: 'Comprehensive release quality assessment'
    },
    {
      id: 'bug-impact',
      title: 'Bug Impact Summary',
      icon: 'fa-bug',
      description: 'Bug statistics from Mantis integration'
    },
    {
      id: 'defect-density',
      title: 'Defect Density Report',
      icon: 'fa-percentage',
      description: 'Test-to-bug ratio and quality indicators'
    }
  ];

  const renderExecutionSummaryReport = () => (
    <>
      <Card className="c-card-1 mb-4">
        <Card.Header className="c-card-header-1">
          <h5 className="mb-0">Overall Metrics</h5>
        </Card.Header>
        <Card.Body className="c-card-body-1">
          <Row>
            <Col md={3}>
              <div className="metric-box">
                <div className="metric-value">45</div>
                <div className="metric-label">Total Executions</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="metric-box">
                <div className="metric-value">678</div>
                <div className="metric-label">Test Cases Executed</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="metric-box">
                <div className="metric-value text-success">89%</div>
                <div className="metric-label">Pass Rate</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="metric-box">
                <div className="metric-value">2h 15m</div>
                <div className="metric-label">Avg Execution Time</div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1">
              <h6 className="mb-0">Execution Breakdown</h6>
            </Card.Header>
            <Card.Body className="c-card-body-1">
              <div className="execution-breakdown">
                <div className="breakdown-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="breakdown-label">
                      <Badge bg="success" className="me-2">Passed</Badge>
                      603 tests
                    </span>
                    <span className="breakdown-percentage">89%</span>
                  </div>
                  <ProgressBar now={89} variant="success" />
                </div>
                <div className="breakdown-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="breakdown-label">
                      <Badge bg="danger" className="me-2">Failed</Badge>
                      52 tests
                    </span>
                    <span className="breakdown-percentage">8%</span>
                  </div>
                  <ProgressBar now={8} variant="danger" />
                </div>
                <div className="breakdown-item">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="breakdown-label">
                      <Badge bg="warning" className="me-2">Blocked</Badge>
                      23 tests
                    </span>
                    <span className="breakdown-percentage">3%</span>
                  </div>
                  <ProgressBar now={3} variant="warning" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1">
              <h6 className="mb-0">Top Failing Test Cases</h6>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Case ID</th>
                    <th>Title</th>
                    <th>Failures</th>
                    <th>Last Fail</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><Link to="/qc/test-cases/TC-0147" className="text-primary">TC-0147</Link></td>
                    <td>Password reset</td>
                    <td><Badge bg="danger">8</Badge></td>
                    <td>Dec 14</td>
                  </tr>
                  <tr>
                    <td><Link to="/qc/test-cases/TC-0189" className="text-primary">TC-0189</Link></td>
                    <td>Payment timeout</td>
                    <td><Badge bg="danger">6</Badge></td>
                    <td>Dec 13</td>
                  </tr>
                  <tr>
                    <td><Link to="/qc/test-cases/TC-0201" className="text-primary">TC-0201</Link></td>
                    <td>Search results</td>
                    <td><Badge bg="danger">5</Badge></td>
                    <td>Dec 12</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderPassFailTrendReport = () => (
    <Card className="c-card-1 mb-4">
      <Card.Header className="c-card-header-1">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Pass vs Fail Trend Analysis</h5>
          <div className="report-stats">
            <span className="text-success me-3">
              <i className="fas fa-arrow-up me-1"></i>
              Current Pass Rate: 89%
            </span>
            <span className="text-muted">
              Previous: 85% (↑ 4%)
            </span>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="c-card-body-1">
        <div className="trend-chart-placeholder mb-4">
          <div className="text-center py-5 text-muted">
            <i className="fas fa-chart-line fa-3x mb-3"></i>
            <p>Line chart showing pass/fail trend over 30 days</p>
            <p className="small">(Chart visualization would be implemented with Chart.js or Recharts)</p>
          </div>
        </div>

        <Table responsive>
          <thead>
            <tr>
              <th>Week</th>
              <th>Executions</th>
              <th>Pass Rate</th>
              <th>Fail Rate</th>
              <th>Trend</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Week 1</td>
              <td>12</td>
              <td><Badge bg="success">85%</Badge></td>
              <td><Badge bg="danger">12%</Badge></td>
              <td><span className="text-success"><i className="fas fa-arrow-up"></i></span></td>
            </tr>
            <tr>
              <td>Week 2</td>
              <td>15</td>
              <td><Badge bg="success">87%</Badge></td>
              <td><Badge bg="danger">10%</Badge></td>
              <td><span className="text-success"><i className="fas fa-arrow-up"></i></span></td>
            </tr>
            <tr>
              <td>Week 3</td>
              <td>10</td>
              <td><Badge bg="success">88%</Badge></td>
              <td><Badge bg="danger">9%</Badge></td>
              <td><span className="text-success"><i className="fas fa-arrow-up"></i></span></td>
            </tr>
            <tr>
              <td>Week 4</td>
              <td>8</td>
              <td><Badge bg="success">89%</Badge></td>
              <td><Badge bg="danger">8%</Badge></td>
              <td><span className="text-success"><i className="fas fa-arrow-up"></i></span></td>
            </tr>
          </tbody>
        </Table>

        <div className="insights-box mt-4">
          <h6><i className="fas fa-lightbulb me-2 text-warning"></i>Insights</h6>
          <ul className="mb-0">
            <li>Pass rate improving steadily (+4% over 30 days)</li>
            <li>Execution count decreased in Week 4 (holidays?)</li>
            <li>Top improvement area: Authentication module</li>
          </ul>
        </div>
      </Card.Body>
    </Card>
  );

  const renderBugImpactReport = () => (
    <>
      <div className="alert alert-info mb-4">
        <i className="fas fa-info-circle me-2"></i>
        <strong>Data Source:</strong> MantisBT (Last synced: 5 minutes ago)
      </div>

      <Row>
        <Col md={3}>
          <Card className="c-card-1 mb-4 text-center">
            <Card.Body>
              <h2 className="mb-1">89</h2>
              <p className="text-muted mb-0">Total Bugs</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="c-card-1 mb-4 text-center">
            <Card.Body>
              <h2 className="mb-1 text-warning">18</h2>
              <p className="text-muted mb-0">Open</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="c-card-1 mb-4 text-center">
            <Card.Body>
              <h2 className="mb-1 text-success">48</h2>
              <p className="text-muted mb-0">Resolved</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="c-card-1 mb-4 text-center">
            <Card.Body>
              <h2 className="mb-1 text-info">23</h2>
              <p className="text-muted mb-0">Closed</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="c-card-1 mb-4">
        <Card.Header className="c-card-header-1">
          <h6 className="mb-0">Bugs by Severity</h6>
        </Card.Header>
        <Card.Body className="c-card-body-1">
          <Table responsive>
            <thead>
              <tr>
                <th>Severity</th>
                <th>Open</th>
                <th>Resolved</th>
                <th>Total</th>
                <th>% Resolved</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><Badge bg="danger">Critical</Badge></td>
                <td>2</td>
                <td>5</td>
                <td>7</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <ProgressBar now={71} variant="success" style={{ flex: 1, height: '8px' }} />
                    <span>71%</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td><Badge bg="warning">Major</Badge></td>
                <td>6</td>
                <td>18</td>
                <td>24</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <ProgressBar now={75} variant="success" style={{ flex: 1, height: '8px' }} />
                    <span>75%</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td><Badge bg="info">Minor</Badge></td>
                <td>8</td>
                <td>20</td>
                <td>28</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <ProgressBar now={71} variant="success" style={{ flex: 1, height: '8px' }} />
                    <span>71%</span>
                  </div>
                </td>
              </tr>
              <tr>
                <td><Badge bg="secondary">Trivial</Badge></td>
                <td>2</td>
                <td>5</td>
                <td>7</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <ProgressBar now={71} variant="success" style={{ flex: 1, height: '8px' }} />
                    <span>71%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card className="c-card-1">
        <Card.Header className="c-card-header-1">
          <h6 className="mb-0">Test-to-Bug Ratio</h6>
        </Card.Header>
        <Card.Body className="c-card-body-1">
          <Row>
            <Col md={6}>
              <div className="ratio-metric">
                <h3>678</h3>
                <p className="text-muted">Total Test Executions</p>
              </div>
            </Col>
            <Col md={6}>
              <div className="ratio-metric">
                <h3>89</h3>
                <p className="text-muted">Total Bugs Found</p>
              </div>
            </Col>
          </Row>
          <div className="text-center mt-3">
            <h4 className="text-primary">13.1%</h4>
            <p className="text-muted mb-2">Defect Density</p>
            <Badge bg="success" className="px-3 py-2">
              <i className="fas fa-check-circle me-2"></i>
              Within Normal Range (10-15%)
            </Badge>
          </div>
        </Card.Body>
      </Card>
    </>
  );

  const renderSelectedReport = () => {
    switch (selectedReport) {
      case 'execution-summary':
        return renderExecutionSummaryReport();
      case 'pass-fail-trend':
        return renderPassFailTrendReport();
      case 'bug-impact':
        return renderBugImpactReport();
      default:
        return (
          <Card className="c-card-1">
            <Card.Body className="text-center py-5">
              <i className="fas fa-chart-bar fa-3x text-muted mb-3"></i>
              <h5>Report Coming Soon</h5>
              <p className="text-muted">This report template is under development.</p>
            </Card.Body>
          </Card>
        );
    }
  };

  return (
    <div className="qc-reports">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="fas fa-chart-bar me-2"></i>
            Reports & Summary
          </h1>
          <p className="page-description">Comprehensive QC analytics and insights</p>
        </div>
        <div className="header-actions">
          <Form.Select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className="me-2"
            style={{ width: '200px' }}
          >
            <option value="all">All Projects</option>
            <option value="ecommerce">E-Commerce Platform</option>
            <option value="mobile">Mobile App</option>
            <option value="admin">Admin Portal</option>
          </Form.Select>
          <Form.Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ width: '150px' }}
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="custom">Custom Range</option>
          </Form.Select>
        </div>
      </div>

      <Row>
        {/* Report Templates Sidebar */}
        <Col lg={3}>
          <Card className="c-card-1 mb-4">
            <Card.Header className="c-card-header-1">
              <h6 className="mb-0">Report Templates</h6>
            </Card.Header>
            <Card.Body className="c-card-body-1 p-0">
              <div className="report-templates-list">
                {reportTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`report-template-item ${selectedReport === template.id ? 'active' : ''}`}
                    onClick={() => setSelectedReport(template.id)}
                  >
                    <div className="template-icon">
                      <i className={`fas ${template.icon}`}></i>
                    </div>
                    <div className="template-content">
                      <div className="template-title">{template.title}</div>
                      <div className="template-description">{template.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Report Content */}
        <Col lg={9}>
          <div className="report-actions mb-3 text-end">
            <Button variant="outline-primary" size="sm" className="me-2">
              <i className="fas fa-file-pdf me-2"></i>
              Export PDF
            </Button>
            <Button variant="outline-primary" size="sm" className="me-2">
              <i className="fas fa-file-excel me-2"></i>
              Export Excel
            </Button>
            <Button variant="primary" size="sm">
              <i className="fas fa-envelope me-2"></i>
              Schedule Email
            </Button>
          </div>

          {renderSelectedReport()}
        </Col>
      </Row>
    </div>
  );
};

export default QCReports;
